import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../utils/styles';
import Icon from '../Icon';
import styles from './styles';

const useStyles = createUseStyles(styles, 'SegmentedButtons');
const ICON_SIZE_BY_VARIANT = {
    small: 'smallMedium',
    medium: 'medium',
    large: 'large',
};

const SegmentedButtons = ({
    className: classNameProp,
    classes: classesProp,
    defaultValue,
    isDisabled,
    onChange,
    options = [],
    overrides: overridesProp,
    size = 'medium',
    value,
}) => {
    const theme = useTheme();
    const classes = useClasses(useStyles, classesProp);
    const override = getOverrides(overridesProp, SegmentedButtons.overrides);
    const iconSize = ICON_SIZE_BY_VARIANT[size] || ICON_SIZE_BY_VARIANT.medium;

    const fallbackValue = useMemo(
        () => defaultValue ?? options[0]?.name ?? null,
        [defaultValue, options],
    );
    const [internalValue, setInternalValue] = useState(fallbackValue);
    const selectedValue = value ?? internalValue;

    useEffect(() => {
        if (value !== undefined) return;
        setInternalValue(fallbackValue);
    }, [fallbackValue, value]);

    const handleSelect = useCallback(
        (option) => {
            if (isDisabled || option?.isDisabled) return;

            if (value === undefined) {
                setInternalValue(option.name);
            }

            onChange && onChange(option.name, option);
            option?.onClick && option.onClick(option);
        },
        [isDisabled, onChange, value],
    );

    return (
        <div
            className={classnames(
                classes.root,
                {
                    [classes.small]: size === 'small',
                    [classes.medium]: size === 'medium',
                    [classes.large]: size === 'large',
                },
                classNameProp,
            )}
            {...override.root}
        >
            {(options || []).map((option) => {
                const isSelected = option.name === selectedValue;
                const disabledOption = isDisabled || option.isDisabled;
                const buttonClassName = classnames(classes.segmentButton, {
                    [classes.segmentButtonSelected]: isSelected,
                    [classes.segmentButtonDisabled]: disabledOption,
                });
                const iconColor = isSelected
                    ? theme.colors.actionMajor[500]
                    : theme.colors.actionMinor[500];

                return (
                    <button
                        key={option.name}
                        type="button"
                        aria-label={option.name}
                        aria-pressed={isSelected}
                        className={buttonClassName}
                        onClick={() => handleSelect(option)}
                        disabled={disabledOption}
                        title={option.name}
                        {...(override.segmentButton || {})}
                    >
                        <span className={classes.icon} {...(override.icon || {})}>
                            {typeof option.icon === 'string' ? (
                                <Icon name={option.icon} size={iconSize} color={iconColor} />
                            ) : (
                                option.icon
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

SegmentedButtons.displayName = 'SegmentedButtons';

SegmentedButtons.overrides = ['root', 'segmentButton', 'icon'];

SegmentedButtons.propTypes = {
    className: PropTypes.string,
    classes: PropTypes.object,
    defaultValue: PropTypes.string,
    isDisabled: PropTypes.bool,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
            isDisabled: PropTypes.bool,
            name: PropTypes.string.isRequired,
            onClick: PropTypes.func,
        }),
    ),
    overrides: PropTypes.object,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    value: PropTypes.string,
};

export default memo(SegmentedButtons);
